import {
  ICandidateScreenState,
  ICriteria,
  IScreeningCriterias,
} from '@/interfaces/portfolioOptimization.interface'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { v4 as uuidv4 } from 'uuid'
// Function to calculate the total number of securities
const calculateTotalSecurities = (criteriaList: ICriteria[]): number => {
  return criteriaList?.reduce(
    (total, criteria) => total + (criteria?.noOfSecurities || 0),
    0,
  )
}

// Define the initial state
export const initialState: ICandidateScreenState = {
  isExpanded: false,
  isComplete: false,
  criteriaList: [],
  screeningCriteriasList: [],
  totalSecurities: 0,
  isSubsetMode: false,
  subsetCriteriaList: [],
  subsetScreeningCriteriaList: [],
  subsetTotalSecurities: 0,
}

export const candidateScreenSlice = createSlice({
  name: 'candidateScreen',
  initialState,
  reducers: {
    toggleExpanded: (state) => {
      state.isExpanded = !state.isExpanded
    },
    setComplete: (state, action: PayloadAction<boolean>) => {
      state.isComplete = action.payload
    },
    setSubsetMode: (state, action: PayloadAction<boolean>) => {
      state.isSubsetMode = action.payload
    },
    resetScreening: (state) => {
      state.isExpanded = false
      state.isComplete = false
      state.criteriaList = []
      state.screeningCriteriasList = []
      state.totalSecurities = 0
    },
    resetSubsetScreening: (state) => {
      state.subsetCriteriaList = []
      state.subsetScreeningCriteriaList = []
      state.subsetTotalSecurities = 0
    },
    setScreeningCriteriasList: (
      state,
      action: PayloadAction<IScreeningCriterias[]>,
    ) => {
      if (state.isSubsetMode) {
        state.subsetScreeningCriteriaList = action.payload
      } else {
        state.screeningCriteriasList = action.payload
      }
    },
    setCriteriaList: (state, action: PayloadAction<ICriteria[]>) => {
      if (state.isSubsetMode) {
        state.subsetCriteriaList = action.payload
        state.subsetTotalSecurities = calculateTotalSecurities(action.payload)
      } else {
        state.criteriaList = action.payload
        state.totalSecurities = calculateTotalSecurities(action.payload)
      }
    },
    addScreeningCriteriaToList: (
      state,
      action: PayloadAction<IScreeningCriterias>,
    ) => {
      if (state.isSubsetMode) {
        state.subsetScreeningCriteriaList = [
          ...state.subsetScreeningCriteriaList,
          { ...action.payload, rowKey: uuidv4() },
        ]
      } else {
        state.screeningCriteriasList = [
          ...state.screeningCriteriasList,
          { ...action.payload, rowKey: uuidv4() },
        ]
      }
    },
    removeScreeningCriteriaFromList: (state, action: PayloadAction<string>) => {
      if (state.isSubsetMode) {
        state.subsetScreeningCriteriaList =
          state.subsetScreeningCriteriaList.filter(
            (criteria) => criteria.rowKey !== action.payload,
          )
      } else {
        state.screeningCriteriasList = state.screeningCriteriasList.filter(
          (criteria) => criteria.rowKey !== action.payload,
        )
      }
    },
    addCriteria: (state, action: PayloadAction<ICriteria>) => {
      if (state.isSubsetMode) {
        const updatedSubsetCriteriaListAfterAdd = [
          ...state.subsetCriteriaList,
          { ...action.payload, rowKey: uuidv4() },
        ]
        state.subsetCriteriaList = updatedSubsetCriteriaListAfterAdd
        state.subsetTotalSecurities = calculateTotalSecurities(
          updatedSubsetCriteriaListAfterAdd,
        )
      } else {
        const updatedCriteriaListAfterAdd = [
          ...state.criteriaList,
          { ...action.payload, rowKey: uuidv4() },
        ]
        state.criteriaList = updatedCriteriaListAfterAdd
        state.totalSecurities = calculateTotalSecurities(
          updatedCriteriaListAfterAdd,
        )
      }
    },
    removeCriteria: (state, action: PayloadAction<string>) => {
      if (state.isSubsetMode) {
        const updatedSubsetCriteriaListAfterRemove =
          state.subsetCriteriaList.filter(
            (criteria) => criteria.rowKey !== action.payload,
          )
        state.subsetCriteriaList = updatedSubsetCriteriaListAfterRemove
        state.subsetTotalSecurities = calculateTotalSecurities(
          updatedSubsetCriteriaListAfterRemove,
        )
      } else {
        const updatedCriteriaListAfterRemove = state.criteriaList.filter(
          (criteria) => criteria.rowKey !== action.payload,
        )
        state.criteriaList = updatedCriteriaListAfterRemove
        state.totalSecurities = calculateTotalSecurities(
          updatedCriteriaListAfterRemove,
        )
      }
    },
    updateCriteria: (state, action: PayloadAction<ICriteria>) => {
      if (state.isSubsetMode) {
        const updatedSubsetCriteriaListAfterUpdate =
          state.subsetCriteriaList.map((criteria) =>
            criteria.id === action.payload.id ? action.payload : criteria,
          )
        state.subsetCriteriaList = updatedSubsetCriteriaListAfterUpdate
        state.subsetTotalSecurities = calculateTotalSecurities(
          updatedSubsetCriteriaListAfterUpdate,
        )
      } else {
        const updatedCriteriaListAfterUpdate = state.criteriaList.map(
          (criteria) =>
            criteria.id === action.payload.id ? action.payload : criteria,
        )
        state.criteriaList = updatedCriteriaListAfterUpdate
        state.totalSecurities = calculateTotalSecurities(
          updatedCriteriaListAfterUpdate,
        )
      }
    },
  },
})

export const {
  toggleExpanded,
  setComplete,
  setSubsetMode,
  resetScreening,
  resetSubsetScreening,
  setCriteriaList,
  addCriteria,
  removeCriteria,
  updateCriteria,
  setScreeningCriteriasList,
  addScreeningCriteriaToList,
  removeScreeningCriteriaFromList,
} = candidateScreenSlice.actions

export default candidateScreenSlice.reducer
